import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import InquiryForm from '../components/InquiryForm';
import api from '../api';

export default function DestinationDetail() {
  var params = useParams();
  var [destination, setDestination] = useState(null);
  var [loading, setLoading]         = useState(true);
  var [notFound, setNotFound]       = useState(false);

  useEffect(function() {
    setLoading(true);
    setNotFound(false);
    api.get('/destinations/' + params.slug)
      .then(function(res) { setDestination(res.data); })
      .catch(function() { setNotFound(true); })
      .finally(function() { setLoading(false); });
  }, [params.slug]);

  var displayName = params.slug
    .replace(/-/g, ' ')
    .replace(/\b\w/g, function(c) { return c.toUpperCase(); });

  if (loading) return <Layout><Loader /></Layout>;

  var name        = destination ? destination.name        : displayName;
  var country     = destination ? destination.country     : '';
  var overview    = destination ? destination.overview    : '';
  var bestTime    = destination ? destination.bestTimeToVisit : '';
  var description = destination ? destination.description : '';
  var highlights  = destination ? (destination.highlights || []) : [];

  return (
    <Layout>

      {/* HERO */}
      <div style={{ position: 'relative', overflow: 'hidden' }} className="dest-hero">
        <img
          src={
            destination && destination.heroImage
              ? destination.heroImage
              : 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1400&q=80'
          }
          alt={name}
          style={{ width: '100%', objectFit: 'cover', display: 'block' }}
          className="dest-hero-img"
        />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(17,45,78,0.82) 0%, rgba(17,45,78,0.2) 60%, transparent 100%)'
        }} />
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '28px 24px'
        }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <span style={{
              fontSize: '0.72rem', fontWeight: 700,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: 8
            }}>
              Destination Guide
            </span>
            <h1 style={{ margin: 0, color: '#fff', lineHeight: 1.1 }} className="dest-hero-title">
              {name}
            </h1>
            {country && (
              <p style={{ margin: '6px 0 0', color: 'rgba(255,255,255,0.72)', fontSize: '0.95rem' }}>
                📍 {country}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <section className="section">
        <div className="container">

          {notFound ? (
            <div style={{ maxWidth: 560 }}>
              <p style={{ fontSize: '1rem', opacity: 0.75, lineHeight: 1.75 }}>
                This destination guide is being prepared by our team. In the meantime,
                send us an inquiry and a specialist will reach out with tailored options.
              </p>
              <div style={{ marginTop: 24 }}>
                <InquiryForm key={params.slug} defaultType="holiday" compact />
              </div>
            </div>
          ) : (
            <div className="dest-content-grid">

              {/* LEFT — main content */}
              <div style={{ minWidth: 0 }}>

                {/* Overview */}
                {overview && (
                  <div style={{ marginBottom: 32 }}>
                    <span className="eyebrow">Overview</span>
                    <p style={{ fontSize: '1rem', lineHeight: 1.8, marginTop: 10 }}>
                      {overview}
                    </p>
                  </div>
                )}

                {/* Highlights */}
                {highlights.length > 0 && (
                  <div style={{ marginBottom: 32 }}>
                    <span className="eyebrow">Highlights</span>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: 10, marginTop: 14
                    }}>
                      {highlights.map(function(h) {
                        return (
                          <div key={h} style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            background: 'var(--bj-cream-soft)',
                            borderRadius: 8, padding: '10px 14px'
                          }}>
                            <span style={{
                              width: 22, height: 22, borderRadius: '50%',
                              background: 'var(--bj-sand)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.65rem', color: 'var(--bj-clay)',
                              fontWeight: 700, flexShrink: 0
                            }}>✓</span>
                            <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>{h}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Best time */}
                {bestTime && (
                  <div style={{ marginBottom: 32 }}>
                    <span className="eyebrow">Best Time to Visit</span>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      background: 'var(--bj-sand)',
                      borderRadius: 10, padding: '14px 18px', marginTop: 12
                    }}>
                      <span style={{ fontSize: '1.3rem' }}>🗓️</span>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: '0.95rem' }}>{bestTime}</p>
                    </div>
                  </div>
                )}

                {/* Description */}
                {description && (
                  <div style={{ marginBottom: 32 }}>
                    <div className="flight-path" style={{ marginBottom: 28 }} />
                    <span className="eyebrow">About {name}</span>
                    <div style={{
                      marginTop: 16,
                      padding: '24px 24px',
                      background: '#fff',
                      borderRadius: 14,
                      boxShadow: '0 4px 20px rgba(17,45,78,0.07)',
                      borderLeft: '4px solid var(--bj-clay)'
                    }}>
                      {description.split('\n\n').map(function(para, i) {
                        return (
                          <p key={i} style={{
                            margin: i === 0 ? 0 : '16px 0 0',
                            fontSize: '0.95rem',
                            lineHeight: 1.85,
                            color: 'var(--bj-espresso)',
                            opacity: 0.85
                          }}>
                            {para}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT — inquiry form */}
              <div className="dest-form-col">
                <div style={{
                  background: 'var(--bj-cream-soft)',
                  borderRadius: 14, padding: '20px 20px 4px'
                }}>
                  <p style={{ margin: '0 0 14px', fontWeight: 700, fontSize: '0.95rem' }}>
                    Interested in {name}?
                  </p>
                  <InquiryForm key={params.slug} defaultType="holiday" compact />
                </div>
              </div>

            </div>
          )}
        </div>
      </section>

      <style>{`
        /* Hero image height */
        .dest-hero-img { height: 420px; }
        .dest-hero-title { font-size: clamp(1.8rem, 5vw, 2.8rem); }

        /* Content grid — desktop: side by side */
        .dest-content-grid {
          display: grid;
          grid-template-columns: 1.3fr 0.7fr;
          gap: 48px;
          align-items: start;
        }
        .dest-form-col {
          position: sticky;
          top: 90px;
        }

        /* Tablet */
        @media (max-width: 900px) {
          .dest-content-grid {
            grid-template-columns: 1fr 0.85fr;
            gap: 32px;
          }
        }

        /* Mobile */
        @media (max-width: 640px) {
          .dest-hero-img { height: 280px !important; }
          .dest-hero-title { font-size: 1.8rem !important; }

          /* Stack content then form */
          .dest-content-grid {
            grid-template-columns: 1fr !important;
            gap: 28px !important;
          }

          /* Form comes after content, no sticky */
          .dest-form-col {
            position: static !important;
            top: auto !important;
          }

          /* Description card padding */
          .dest-content-grid .card,
          .dest-content-grid [style*="borderLeft"] {
            padding: 16px !important;
          }
        }
      `}</style>
    </Layout>
  );
}